/*
 * HYETECH: Split Promotional Hero Carousel (sections/split-promo-hero.liquid)
 * Lightweight vanilla JS, no dependencies.
 * - Native CSS scroll-snap handles touch / trackpad swiping
 * - Dots, prev/next arrows, keyboard arrows, play/pause
 * - Autoplay pauses on hover, focus, off-screen and hidden tabs; stops
 *   after any manual navigation; disabled for prefers-reduced-motion
 * - Theme editor: selecting a slide block jumps to it
 */
(function () {
  if (customElements.get('split-promo-carousel')) return;

  class SplitPromoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-promo-track]');
      this.slides = Array.from(this.querySelectorAll('[data-promo-slide]'));
      if (!this.track || this.slides.length < 2) return;

      this.dots = Array.from(this.querySelectorAll('[data-promo-dot]'));
      this.prevBtn = this.querySelector('[data-promo-prev]');
      this.nextBtn = this.querySelector('[data-promo-next]');
      this.toggleBtn = this.querySelector('[data-promo-toggle]');

      this.index = 0;
      this.target = null;
      this.timer = null;
      this.pauses = new Set();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.delay = Math.max(2000, (parseFloat(this.dataset.speed) || 5) * 1000);
      this.autoplayAllowed = this.dataset.autoplay === 'true';
      this.playing = this.autoplayAllowed && !this.reducedMotion.matches;

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onVisibility = this.onVisibility.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('keydown', this.onKeydown);

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i, true)));
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1, true));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1, true));

      if (this.autoplayAllowed) {
        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => (this.playing ? this.stop() : this.play()));
        }
        this.addEventListener('mouseenter', () => this.pause('hover'));
        this.addEventListener('mouseleave', () => this.resume('hover'));
        this.addEventListener('focusin', () => this.pause('focus'));
        this.addEventListener('focusout', (e) => {
          if (!this.contains(e.relatedTarget)) this.resume('focus');
        });
        // A swipe / drag on the slides counts as user interaction
        this.track.addEventListener('pointerdown', () => this.stop(), { passive: true });
        this.track.addEventListener('touchstart', () => this.stop(), { passive: true });
        this.track.addEventListener('wheel', (e) => {
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) this.stop();
        }, { passive: true });

        document.addEventListener('visibilitychange', this.onVisibility);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => (entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')));
          }, { threshold: 0.35 });
          this.observer.observe(this);
        }
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.update(0);
      this.updateToggle();
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.targetTimer);
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    get count() {
      return this.slides.length;
    }

    currentFromScroll() {
      const width = this.track.clientWidth || 1;
      return Math.min(this.count - 1, Math.max(0, Math.round(Math.abs(this.track.scrollLeft) / width)));
    }

    onScroll() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        const i = this.currentFromScroll();
        if (this.target !== null) {
          // Ignore intermediate positions while a programmatic scroll runs
          if (i !== this.target) return;
          this.target = null;
        }
        if (i !== this.index) {
          this.update(i);
          this.schedule();
        }
      });
    }

    goTo(i, byUser) {
      const next = (i + this.count) % this.count;
      if (byUser) this.stop();
      this.target = next;
      clearTimeout(this.targetTimer);
      this.targetTimer = setTimeout(() => (this.target = null), 1200);
      const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollTo({
        left: dir * next * this.track.clientWidth,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.update(next);
      this.schedule();
    }

    update(i) {
      this.index = i;
      this.slides.forEach((slide, k) => {
        const active = k === i;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
      this.dots.forEach((dot, k) => {
        if (k === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.playing || this.pauses.size) return;
      this.timer = setTimeout(() => this.goTo(this.index + 1, false), this.delay);
    }

    pause(reason) {
      this.pauses.add(reason);
      clearTimeout(this.timer);
    }

    resume(reason) {
      this.pauses.delete(reason);
      this.schedule();
    }

    stop() {
      if (!this.playing) return;
      this.playing = false;
      clearTimeout(this.timer);
      this.updateToggle();
    }

    play() {
      this.playing = true;
      this.pauses.delete('hover');
      this.pauses.delete('focus');
      this.updateToggle();
      this.schedule();
    }

    updateToggle() {
      this.track.setAttribute('aria-live', this.playing ? 'off' : 'polite');
      if (!this.toggleBtn) return;
      this.toggleBtn.classList.toggle('is-paused', !this.playing);
      this.toggleBtn.setAttribute(
        'aria-label',
        this.playing ? this.dataset.labelPause || 'Pause slideshow' : this.dataset.labelPlay || 'Play slideshow'
      );
    }

    onKeydown(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const focusInSlide = this.track.contains(document.activeElement);
      this.goTo(this.index + (e.key === 'ArrowLeft' ? -1 : 1), true);
      if (focusInSlide) {
        const link = this.slides[this.index].querySelector('a');
        if (link) link.focus({ preventScroll: true });
      }
    }

    onVisibility() {
      if (document.hidden) this.pause('hidden');
      else this.resume('hidden');
    }

    onBlockSelect(e) {
      const i = this.slides.indexOf(e.target);
      if (i > -1) this.goTo(i, true);
    }
  }

  customElements.define('split-promo-carousel', SplitPromoCarousel);
})();
